import { useEffect, useMemo, useState, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Loader2, ExternalLink, Filter, RefreshCcw, ChevronDown, ChevronRight,
  ArrowUpCircle, ArrowDownCircle,
} from 'lucide-react';
import {
  getTriageToday,
  type TriageRow,
  type TriageTodayResponse,
} from '../lib/api/triage.js';

type FilterMode =
  | 'all'
  | 'picked'
  | 'editorial'
  | 'signal'
  | 'drop'
  | 'fast_lane'
  | 'has_article'
  | 'promoted'
  | 'demoted';

interface DiscoveryTriagePanelProps {
  /** Exchange tab. Default 'ASX' — only ASX is wired up. */
  exchange?: string;
  /** Trading-day window (default 2). */
  days?: number;
  /** Optional pinned end-of-window date (YYYY-MM-DD). */
  scanDate?: string;

  /** When true, render row checkboxes + inline actions and lift selection up. */
  selectionEnabled?: boolean;
  selectedIds?: Set<string>;
  onToggleSelected?: (scanId: string) => void;
  onSelectAllVisible?: (scanIds: string[]) => void;
  onPromoteRow?: (row: TriageRow) => void;
  onDemoteRow?: (row: TriageRow) => void;

  /** Render a section header per scan_date instead of a flat list. */
  groupedByDate?: boolean;

  /** Hide the per-route filter strip (e.g. when the page provides its own). */
  hideFilters?: boolean;

  /** Notified whenever the fetched rows change — used by the page to
   * resolve scan_id → announcement_id for bulk actions. */
  onRowsChange?: (rows: TriageRow[]) => void;
}

/**
 * Operator triage view: broad-scan rows with stage-1/2 status,
 * intelligence_signal, discovery picks, and any generated articles.
 *
 * In read-only mode (no selection callbacks) it powers the legacy
 * Tools-page embed. With selectionEnabled+callbacks it powers the
 * Triage page, exposing per-row Promote / Demote and bulk-selection.
 */
export function DiscoveryTriagePanel({
  exchange = 'ASX',
  days = 2,
  scanDate,
  selectionEnabled = false,
  selectedIds,
  onToggleSelected,
  onSelectAllVisible,
  onPromoteRow,
  onDemoteRow,
  groupedByDate = false,
  hideFilters = false,
  onRowsChange,
}: DiscoveryTriagePanelProps) {
  const [filter, setFilter] = useState<FilterMode>('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const triageQuery = useQuery<TriageTodayResponse>({
    queryKey: ['triage-today', exchange, days, scanDate ?? null],
    queryFn: () => getTriageToday({ exchange, days, scanDate }),
    refetchInterval: 30_000,
  });

  const rows = triageQuery.data?.rows ?? [];

  useEffect(() => {
    if (onRowsChange) onRowsChange(rows);
  }, [rows, onRowsChange]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === 'picked' && !r.discovery_picked_at) return false;
      if (filter === 'editorial' && r.stage1_route !== 'editorial' && r.stage1_route !== 'both') return false;
      if (filter === 'signal' && r.stage1_route !== 'signal' && r.stage1_route !== 'both') return false;
      if (filter === 'drop' && r.stage1_route !== 'drop') return false;
      if (filter === 'fast_lane' && !r.skipped_fast_lane) return false;
      if (filter === 'has_article' && (!r.articles || r.articles.length === 0)) return false;
      if (filter === 'promoted' && r.operator_action !== 'promote') return false;
      if (filter === 'demoted' && r.operator_action !== 'demote') return false;
      if (q) {
        const hay = [
          r.ticker, r.title, r.entity_name, r.stage2_sector, r.stage2_rationale,
          r.signal_label, r.signal_original_type,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filter, search]);

  const grouped = useMemo(() => {
    if (!groupedByDate) {
      return [{ date: 'all', rows: filtered }];
    }
    const map = new Map<string, TriageRow[]>();
    for (const r of filtered) {
      const arr = map.get(r.scan_date) ?? [];
      arr.push(r);
      map.set(r.scan_date, arr);
    }
    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([date, rows]) => ({ date, rows }));
  }, [filtered, groupedByDate]);

  const summary = triageQuery.data?.summary;
  const dateWindow = triageQuery.data?.date_window;
  const perDay = triageQuery.data?.per_day ?? {};
  const macroContext = triageQuery.data?.macro_context ?? null;
  const activeThemes = triageQuery.data?.active_themes ?? [];

  if (triageQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 text-grey-400 animate-spin" />
      </div>
    );
  }

  if (triageQuery.isError) {
    return (
      <div className="text-sm text-red-600 py-6">
        Failed to load triage rows: {(triageQuery.error as Error).message}
      </div>
    );
  }

  const visibleScanIds = filtered.map((r) => r.scan_id);
  const allVisibleSelected = selectionEnabled && selectedIds && visibleScanIds.length > 0
    && visibleScanIds.every((id) => selectedIds.has(id));
  const colSpan = selectionEnabled ? 11 : 9;

  return (
    <div className="space-y-4">
      {(macroContext || activeThemes.length > 0) && (
        <div className="rounded-md border border-primary-200 bg-primary-50/40 px-3 py-2 text-xs space-y-1">
          {macroContext?.priority_sectors && macroContext.priority_sectors.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="font-semibold text-primary-800">
                Priority sectors:
              </span>
              {macroContext.priority_sectors.map((s) => (
                <span
                  key={s}
                  className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px]"
                >
                  {s}
                </span>
              ))}
            </div>
          )}
          {macroContext?.macro_themes && macroContext.macro_themes.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="font-semibold text-primary-800">
                Macro themes:
              </span>
              {macroContext.macro_themes.map((t) => (
                <span
                  key={t}
                  className="inline-block px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px]"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
          {activeThemes.length > 0 && (
            <div>
              <div className="font-semibold text-primary-800 mb-0.5">
                Active editorial themes
              </div>
              <ul className="space-y-0.5">
                {activeThemes.slice(0, 6).map((t) => (
                  <li key={t.theme_id} className="text-[11px] text-grey-800">
                    <span
                      className={
                        'inline-block w-12 px-1 mr-1.5 rounded text-center text-[9px] uppercase font-semibold ' +
                        (t.horizon === 'daily'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-purple-100 text-purple-800')
                      }
                    >
                      {t.horizon}
                    </span>
                    <span className="font-medium">{t.title}</span>
                    {t.importance !== null && (
                      <span className="ml-1 text-grey-500">
                        ({t.momentum} · {t.importance.toFixed(2)})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {summary && (
        <>
          {dateWindow && (
            <div className="text-xs text-grey-600 flex items-center gap-2 flex-wrap">
              <span className="font-medium text-grey-700">Window:</span>
              {dateWindow.trading_days.map((d) => (
                <span
                  key={d}
                  className="font-mono px-1.5 py-0.5 rounded border border-grey-200 bg-surface"
                >
                  {d}
                  {perDay[d] && (
                    <span className="ml-1 text-grey-500">
                      · {perDay[d].total_rows} scans · {perDay[d].with_article} articles
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
            <SummaryTile label="Scanned" value={summary.total_rows} />
            <SummaryTile label="Editorial" value={(summary.stage1_routes['editorial'] || 0) + (summary.stage1_routes['both'] || 0)} />
            <SummaryTile label="Signal" value={(summary.stage1_routes['signal'] || 0) + (summary.stage1_routes['both'] || 0)} />
            <SummaryTile label="Dropped" value={summary.stage1_routes['drop'] || 0} />
            <SummaryTile label="Fast-lane skip" value={summary.fast_lane_skipped} />
            <SummaryTile label="Picked" value={summary.discovery_picked} accent />
            <SummaryTile label="Article" value={summary.with_article} accent />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 text-xs">
            <SummaryTile label="Signals routed" value={summary.signal_routed} />
            <SummaryTile label="Signals written" value={summary.signal_written} />
            <SummaryTile
              label="Signals unwritten"
              value={summary.signal_route_unwritten}
            />
            <SummaryTile label="Blocklist drops" value={summary.blocklist_drops} />
            <SummaryTile label="Promoted (op)" value={summary.promoted_by_operator} />
            <SummaryTile label="Demoted (op)" value={summary.demoted_by_operator} />
          </div>
        </>
      )}

      {!hideFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 text-xs text-grey-600">
            <Filter className="h-3.5 w-3.5" />
            <span>Filter</span>
          </div>
          <FilterButton current={filter} value="all" onClick={setFilter}>All</FilterButton>
          <FilterButton current={filter} value="picked" onClick={setFilter}>Picked</FilterButton>
          <FilterButton current={filter} value="has_article" onClick={setFilter}>Article</FilterButton>
          <FilterButton current={filter} value="editorial" onClick={setFilter}>Editorial</FilterButton>
          <FilterButton current={filter} value="signal" onClick={setFilter}>Signal</FilterButton>
          <FilterButton current={filter} value="drop" onClick={setFilter}>Drop</FilterButton>
          <FilterButton current={filter} value="fast_lane" onClick={setFilter}>Fast-lane</FilterButton>
          <FilterButton current={filter} value="promoted" onClick={setFilter}>Promoted</FilterButton>
          <FilterButton current={filter} value="demoted" onClick={setFilter}>Demoted</FilterButton>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ticker, title, entity, sector…"
            className="ml-2 px-2 py-1 border border-grey-300 rounded text-xs flex-1 min-w-[200px]"
          />
          <button
            onClick={() => triageQuery.refetch()}
            className="flex items-center gap-1 px-2 py-1 text-xs border border-grey-300 rounded hover:bg-grey-50"
            title="Refresh now"
          >
            <RefreshCcw className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <div className="overflow-x-auto border border-grey-200 rounded-md">
        <table className="min-w-full text-xs">
          <thead className="bg-grey-50 text-grey-700">
            <tr>
              {selectionEnabled && (
                <th className="text-left px-2 py-2 font-medium w-6">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={() => onSelectAllVisible?.(visibleScanIds)}
                    title="Select all visible"
                  />
                </th>
              )}
              <th className="text-left px-2 py-2 font-medium w-6"></th>
              <th className="text-left px-2 py-2 font-medium">Ticker</th>
              <th className="text-left px-2 py-2 font-medium">Entity</th>
              <th className="text-left px-2 py-2 font-medium min-w-[280px]">Title</th>
              <th className="text-left px-2 py-2 font-medium">Stage 1</th>
              <th className="text-left px-2 py-2 font-medium">Stage 2</th>
              <th className="text-left px-2 py-2 font-medium">Signal</th>
              <th className="text-left px-2 py-2 font-medium">Picked</th>
              <th className="text-left px-2 py-2 font-medium">Status</th>
              {selectionEnabled && (
                <th className="text-right px-2 py-2 font-medium">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {grouped.map((group) => (
              <Fragment key={group.date}>
                {groupedByDate && (
                  <tr className="bg-primary-50/40 border-t border-grey-200">
                    <td colSpan={colSpan} className="px-3 py-1.5 text-[11px] font-semibold text-primary-800">
                      {formatGroupDate(group.date)}
                      <span className="ml-2 font-normal text-grey-600">
                        {group.rows.length} {group.rows.length === 1 ? 'row' : 'rows'}
                      </span>
                    </td>
                  </tr>
                )}
                {group.rows.map((r) => (
                  <TriageTableRow
                    key={r.scan_id}
                    row={r}
                    expanded={!!expanded[r.scan_id]}
                    onToggle={() =>
                      setExpanded((p) => ({ ...p, [r.scan_id]: !p[r.scan_id] }))
                    }
                    selectionEnabled={selectionEnabled}
                    selected={!!selectedIds?.has(r.scan_id)}
                    onSelectToggle={() => onToggleSelected?.(r.scan_id)}
                    onPromote={onPromoteRow ? () => onPromoteRow(r) : undefined}
                    onDemote={onDemoteRow ? () => onDemoteRow(r) : undefined}
                  />
                ))}
              </Fragment>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={colSpan} className="text-center py-6 text-grey-500">
                  No matching rows.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-grey-500">
        Showing {filtered.length} of {rows.length} rows · auto-refreshes every 30s
      </div>
    </div>
  );
}

function formatGroupDate(d: string) {
  try {
    const dt = new Date(d + 'T00:00:00');
    return `${format(dt, 'EEEE, d MMM yyyy')}`;
  } catch {
    return d;
  }
}

function SummaryTile({
  label, value, accent,
}: { label: string; value: number; accent?: boolean }) {
  return (
    <div
      className={
        'rounded-md border px-2 py-1.5 ' +
        (accent
          ? 'border-primary-200 bg-primary-50 text-primary-900'
          : 'border-grey-200 bg-surface text-grey-700')
      }
    >
      <div className="font-mono text-base leading-tight">{value}</div>
      <div className="uppercase tracking-wide text-[10px]">{label}</div>
    </div>
  );
}

function FilterButton({
  current, value, onClick, children,
}: {
  current: FilterMode;
  value: FilterMode;
  onClick: (v: FilterMode) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      onClick={() => onClick(value)}
      className={
        'px-2 py-1 rounded text-xs border ' +
        (active
          ? 'bg-primary-600 text-white border-primary-600'
          : 'bg-surface text-grey-700 border-grey-300 hover:bg-grey-50')
      }
    >
      {children}
    </button>
  );
}

function TriageTableRow({
  row, expanded, onToggle,
  selectionEnabled, selected, onSelectToggle,
  onPromote, onDemote,
}: {
  row: TriageRow;
  expanded: boolean;
  onToggle: () => void;
  selectionEnabled: boolean;
  selected: boolean;
  onSelectToggle: () => void;
  onPromote?: () => void;
  onDemote?: () => void;
}) {
  const Caret = expanded ? ChevronDown : ChevronRight;
  const colSpan = selectionEnabled ? 11 : 9;

  // Disable rules
  const allPublished = row.articles.length > 0 && row.articles.every(a => a.publish_status === 'published');
  const announcementId = row.articles.find(a => a.announcement_id)?.announcement_id ?? null;
  const promoteDisabled = allPublished;
  const demoteDisabled = !announcementId || row.articles.length === 0;

  return (
    <>
      <tr className={`border-t border-grey-100 hover:bg-grey-50/50 ${selected ? 'bg-primary-50/30' : ''}`}>
        {selectionEnabled && (
          <td className="px-2 py-1.5 align-top">
            <input
              type="checkbox"
              checked={selected}
              onChange={onSelectToggle}
            />
          </td>
        )}
        <td className="px-2 py-1.5 align-top">
          <button onClick={onToggle} className="text-grey-500 hover:text-grey-900">
            <Caret className="h-3.5 w-3.5" />
          </button>
        </td>
        <td className="px-2 py-1.5 font-mono align-top">
          {row.ticker}
          {row.price_sensitive && <span className="ml-1 text-amber-600 font-bold" title="Price sensitive">$</span>}
        </td>
        <td className="px-2 py-1.5 align-top">
          {row.entity_name ? (
            <span className="text-grey-800">{row.entity_name}</span>
          ) : (
            <span className="text-grey-400 italic">unresolved</span>
          )}
          {row.entity_discovery_status === 'auto_stub' && (
            <span className="ml-1 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px]">stub</span>
          )}
        </td>
        <td className="px-2 py-1.5 align-top">
          <div className="line-clamp-2 max-w-[420px]" title={row.title}>{row.title}</div>
          {row.skipped_fast_lane && (
            <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px]">fast-lane</span>
          )}
        </td>
        <td className="px-2 py-1.5 align-top whitespace-nowrap">
          <Stage1Badge route={row.stage1_route} />
          {row.stage1_signal_type && (
            <div className="text-[10px] text-grey-500">{row.stage1_signal_type}</div>
          )}
          {row.stage1_route === 'drop' && row.stage2_rationale === 'pattern_blocklist' && (
            <div className="text-[10px] text-grey-500 italic">blocklist</div>
          )}
        </td>
        <td className="px-2 py-1.5 align-top whitespace-nowrap">
          <div className="flex items-center gap-1">
            <ContentTypeBadge ct={row.stage2_content_type} />
            <Stage2ScoreChip score={row.stage2_score} />
          </div>
          {row.stage2_sector && (
            <div className="text-[10px] text-grey-500">{row.stage2_sector}</div>
          )}
        </td>
        <td className="px-2 py-1.5 align-top">
          {row.signal_id ? (
            <div className="text-[10px]">
              <div className="text-grey-800">{row.signal_label || row.signal_db_type}</div>
              {row.signal_original_type && row.signal_original_type !== row.signal_db_type && (
                <div className="text-grey-500">({row.signal_original_type})</div>
              )}
            </div>
          ) : (
            <span className="text-grey-400">—</span>
          )}
        </td>
        <td className="px-2 py-1.5 align-top">
          {row.discovery_picked_at ? (
            <div className="text-[10px]">
              <div className="text-emerald-700 font-medium">picked</div>
              {row.picks[0] && (
                <div className="text-grey-500">
                  rank {row.picks[0].rank ?? '—'} · {row.picks[0].target_name || ''}
                </div>
              )}
            </div>
          ) : (
            <span className="text-grey-400">—</span>
          )}
        </td>
        <td className="px-2 py-1.5 align-top">
          <StatusPill row={row} />
          {row.articles.length > 0 && (
            <ul className="space-y-0.5 mt-1">
              {row.articles.map((a) => (
                <li key={a.article_id} className="text-[10px]">
                  <a
                    href={`/articles/${a.article_id}`}
                    className="text-primary-700 hover:underline inline-flex items-center gap-0.5"
                  >
                    <span className="line-clamp-1 max-w-[180px]">{a.title || 'Untitled'}</span>
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  </a>
                  <span className="ml-1 text-grey-500">({a.publish_status || 'draft'})</span>
                  {a.is_secondary && (
                    <span className="ml-1 inline-block px-1 rounded bg-amber-100 text-amber-800 text-[9px]" title="Alternate-angle article kept as draft">
                      secondary
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </td>
        {selectionEnabled && (
          <td className="px-2 py-1.5 align-top whitespace-nowrap text-right">
            <div className="inline-flex items-center gap-1">
              <button
                onClick={onPromote}
                disabled={promoteDisabled || !onPromote}
                className="p-1 rounded hover:bg-emerald-50 text-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed"
                title={promoteDisabled ? 'All articles already published' : 'Promote'}
              >
                <ArrowUpCircle className="h-4 w-4" />
              </button>
              <button
                onClick={onDemote}
                disabled={demoteDisabled || !onDemote}
                className="p-1 rounded hover:bg-amber-50 text-amber-700 disabled:opacity-30 disabled:cursor-not-allowed"
                title={demoteDisabled ? 'No announcement to demote' : 'Demote'}
              >
                <ArrowDownCircle className="h-4 w-4" />
              </button>
            </div>
          </td>
        )}
      </tr>
      {expanded && (
        <tr className="bg-grey-50/40 border-t border-grey-100">
          <td colSpan={colSpan} className="px-4 py-3 text-[11px] text-grey-700">
            <ExpandedDetail row={row} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetail({ row }: { row: TriageRow }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
      <Field label="Scan ID" value={row.scan_id} mono />
      <Field
        label="Announced"
        value={row.announcement_time ? format(new Date(row.announcement_time), 'PP HH:mm') : '—'}
      />
      <Field label="PDF" value={row.pdf_url ? <a href={row.pdf_url} className="text-primary-700 hover:underline">open</a> : '—'} />
      <Field label="Stage 1 direction" value={row.stage1_direction || '—'} />
      <Field
        label="Stage 2 rationale"
        value={row.stage2_rationale || '—'}
        wide
      />
      <Field
        label="Discovery picked at"
        value={row.discovery_picked_at ? format(new Date(row.discovery_picked_at), 'PP HH:mm:ss') : '—'}
      />
      {row.operator_action && (
        <>
          <Field label="Operator action" value={row.operator_action} />
          <Field
            label="Operator action at"
            value={row.operator_action_at ? format(new Date(row.operator_action_at), 'PP HH:mm:ss') : '—'}
          />
          <Field label="Operator" value={row.operator_action_by || '—'} />
          <Field
            label="Operator reason"
            value={row.operator_action_reason || '—'}
            wide
          />
        </>
      )}
      {row.picks.length > 0 && (
        <div className="col-span-full">
          <div className="text-[10px] uppercase tracking-wide text-grey-500 mb-1">Picks</div>
          <ul className="space-y-1">
            {row.picks.map((p) => (
              <li key={p.candidate_id} className="font-mono text-[10px]">
                #{p.rank} · {p.action} · score {p.score?.toFixed(3)} · target {p.target_name || p.target_id?.slice(0, 8)} · articles [{p.article_ids.length}]
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Field({
  label, value, mono, wide,
}: { label: string; value: React.ReactNode; mono?: boolean; wide?: boolean }) {
  return (
    <div className={wide ? 'col-span-2 md:col-span-3' : ''}>
      <div className="text-[10px] uppercase tracking-wide text-grey-500">{label}</div>
      <div className={mono ? 'font-mono text-[11px] break-all' : 'text-[11px]'}>{value}</div>
    </div>
  );
}

function Stage1Badge({ route }: { route: string | null }) {
  if (!route) return <span className="text-grey-400 text-[10px]">—</span>;
  const styles: Record<string, string> = {
    editorial: 'bg-emerald-100 text-emerald-800',
    signal: 'bg-blue-100 text-blue-800',
    both: 'bg-purple-100 text-purple-800',
    drop: 'bg-grey-200 text-grey-600',
    fast_lane: 'bg-amber-100 text-amber-800',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] ${styles[route] || 'bg-grey-100 text-grey-700'}`}>
      {route}
    </span>
  );
}

function ContentTypeBadge({ ct }: { ct: string | null }) {
  if (!ct) return <span className="text-grey-400 text-[10px]">—</span>;
  const styles: Record<string, string> = {
    article: 'bg-emerald-100 text-emerald-800',
    activity_item: 'bg-amber-100 text-amber-800',
    digest_candidate: 'bg-grey-200 text-grey-600',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] ${styles[ct] || 'bg-grey-100 text-grey-700'}`}>
      {ct}
    </span>
  );
}

function Stage2ScoreChip({ score }: { score: number | null }) {
  if (score === null || score === undefined) {
    return <span className="text-grey-400 text-[10px]">—</span>;
  }
  const n = Number(score);
  let cls = 'bg-grey-100 text-grey-600';
  if (n >= 0.7) cls = 'bg-emerald-100 text-emerald-800';
  else if (n >= 0.4) cls = 'bg-amber-100 text-amber-800';
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${cls}`}>
      {n.toFixed(2)}
    </span>
  );
}

function StatusPill({ row }: { row: TriageRow }) {
  const articles = row.articles ?? [];
  const has = articles.length > 0;
  const anyPublished = articles.some(a => a.publish_status === 'published');
  const allDraft = has && articles.every(a => a.publish_status === 'draft');
  const allArchived = has && articles.every(a => a.publish_status === 'archived');

  let label = 'NEW';
  let cls = 'bg-surface text-grey-700 border border-grey-300';

  if (has && anyPublished) {
    label = 'PUBLISHED';
    cls = 'bg-emerald-100 text-emerald-800 border border-emerald-300';
  } else if (has && row.operator_action === 'demote' && allDraft) {
    label = 'DEMOTED';
    cls = 'bg-surface text-amber-800 border border-amber-400';
  } else if (allArchived) {
    label = 'ARCHIVED';
    cls = 'bg-grey-200 text-grey-700 border border-grey-300';
  } else if (has) {
    label = 'DRAFT';
    cls = 'bg-grey-100 text-grey-700 border border-grey-300';
  } else if (row.operator_action === 'promote' && !has) {
    label = 'GENERATING';
    cls = 'bg-blue-100 text-blue-800 border border-blue-300 animate-pulse';
  } else if (row.discovery_picked_at && !has) {
    label = 'PICKED';
    cls = 'bg-primary-100 text-primary-800 border border-primary-300';
  } else if (row.stage1_route === 'drop' || row.skipped_fast_lane) {
    label = 'SKIPPED';
    cls = 'bg-grey-100 text-grey-500 border border-grey-200';
  }

  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>
      {label}
    </span>
  );
}
